import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { fetchData } from "../api/client";
import { AWARENESS_UNSET_GROUP_STAGE_ID } from "../data/awarenessPipeline/constants";
import { collectAwarenessGroupIds } from "../utils/awarenessGroupTree";
import { clientMatchesAwarenessPersonFilter } from "../utils/awarenessPersonFilter";

const PIPELINE_URL = "/clients-groups/pipeline";

const safeGetStagesFromResponse = (resp) => {
  if (!resp) return [];
  const payload = resp.data ?? resp;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.stages)) return payload.stages;
  if (payload?.data && Array.isArray(payload.data.stages)) return payload.data.stages;
  return [];
};

const normalizeClientsList = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    if (Array.isArray(raw.data)) return raw.data;
    if (Array.isArray(raw.clients)) return raw.clients;
  }
  return [];
};

const personTypeQuery = (personType) => {
  const v = String(personType || "").trim().toLowerCase();
  return v === "pf" || v === "pj" ? v : "";
};

const MAX_EMPTY_FILTER_PAGES = 20;
const MIN_CLIENT_SEARCH_LENGTH = 3;

const effectiveClientSearch = (search) => {
  const v = String(search || "").trim();
  return v.length >= MIN_CLIENT_SEARCH_LENGTH ? v : "";
};

const mergeClientListParams = (qs, { tipoPessoa, search, filtersQuery } = {}) => {
  if (tipoPessoa) qs.set("tipo_pessoa", tipoPessoa);
  const term = effectiveClientSearch(search);
  if (term) qs.set("search", term);
  if (!filtersQuery) return qs;
  const extra = new URLSearchParams(String(filtersQuery).replace(/^\?/, ""));
  extra.forEach((value, key) => {
    if (!qs.has(key) && value) qs.set(key, value);
  });
  return qs;
};

export const useAwarenessPipelineStages = (
  personType = "all",
  { search = "", filtersQuery = "" } = {}
) => {
  const tipoPessoa = personTypeQuery(personType);
  const term = effectiveClientSearch(search);
  return useQuery(
    ["awareness-pipeline-stages", tipoPessoa || "all", term, filtersQuery || ""],
    async () => {
      const qs = mergeClientListParams(new URLSearchParams(), {
        tipoPessoa,
        search: term,
        filtersQuery,
      });
      const suffix = qs.toString() ? `?${qs.toString()}` : "";
      const resp = await fetchData(`${PIPELINE_URL}${suffix}`, "GET");
      return safeGetStagesFromResponse(resp);
    },
    {
      staleTime: 5000,
      refetchOnWindowFocus: false,
    }
  );
};

export const useAwarenessPipelineClients = ({
  stages = [],
  limit = 50,
  personType = "all",
  search = "",
  filtersQuery = "",
} = {}) => {
  const [clientsByStage, setClientsByStage] = useState({});
  const [pageByStage, setPageByStage] = useState({});
  const [hasMoreByStage, setHasMoreByStage] = useState({});
  const [loadingByStage, setLoadingByStage] = useState({});
  const [totalByStage, setTotalByStage] = useState({});
  const [error, setError] = useState(null);

  const tipoPessoa = personTypeQuery(personType);
  const searchTerm = effectiveClientSearch(search);
  const filtersKey = String(filtersQuery || "");

  const stageIds = useMemo(
    () => (stages || []).map((s) => Number(s.id)).filter((id) => Number.isFinite(id) || id === AWARENESS_UNSET_GROUP_STAGE_ID),
    [stages]
  );
  const groupIdsByStage = useMemo(() => {
    const map = {};
    (stages || []).forEach((stage) => {
      if (stage?.remainder) {
        map[String(stage.id)] = [Number(stage.id)];
        return;
      }
      const ids = collectAwarenessGroupIds(stage);
      map[String(stage.id)] = ids.length ? ids : [Number(stage.id)];
    });
    return map;
  }, [stages]);
  const stageKey = useMemo(
    () =>
      stageIds
        .map((id) => `${id}:${(groupIdsByStage[String(id)] || []).join("-")}`)
        .join("|"),
    [stageIds, groupIdsByStage]
  );

  const loadPage = useCallback(
    async (stageId, startPage = 1, replace = false) => {
      const stageKeyStr = String(stageId);
      setLoadingByStage((m) => ({ ...m, [stageKeyStr]: true }));

      try {
        const groupIds = groupIdsByStage[stageKeyStr] || [Number(stageId)];
        let page = startPage;
        let accumulated = [];
        let lastPage = startPage;
        let more = false;
        let filteredTotal = null;

        while (page <= startPage + MAX_EMPTY_FILTER_PAGES) {
          const qs = mergeClientListParams(
            new URLSearchParams({
              page: String(page),
              limit: String(limit),
              id_grupo: groupIds.join(","),
            }),
            { tipoPessoa, search: searchTerm, filtersQuery }
          );
          const resp = await fetchData(`/clients?${qs.toString()}`, "GET");
          const rawClients = normalizeClientsList(resp?.data ?? resp);
          const clients = rawClients.filter((client) =>
            clientMatchesAwarenessPersonFilter(client, tipoPessoa)
          );
          lastPage = page;
          const pagination = resp?.pagination || resp?.data?.pagination || {};
          const total = pagination.total != null ? Number(pagination.total) : null;
          if (total != null && Number.isFinite(total)) filteredTotal = total;
          more =
            pagination.hasMore === true ||
            (pagination.hasMore !== false &&
              (total != null && Number.isFinite(total)
                ? page * limit < total
                : rawClients.length === limit));

          if (clients.length) {
            accumulated = accumulated.concat(clients);
            break;
          }
          if (!more) break;
          page += 1;
        }

        setClientsByStage((m) => ({
          ...m,
          [stageKeyStr]: replace
            ? accumulated
            : [...(m[stageKeyStr] || []), ...accumulated],
        }));
        setPageByStage((m) => ({ ...m, [stageKeyStr]: lastPage }));
        setHasMoreByStage((m) => ({
          ...m,
          [stageKeyStr]: Boolean(more && accumulated.length),
        }));
        if (filteredTotal != null) {
          setTotalByStage((m) => ({ ...m, [stageKeyStr]: filteredTotal }));
        } else if (replace) {
          setTotalByStage((m) => ({ ...m, [stageKeyStr]: accumulated.length }));
        }
      } catch (e) {
        console.error("Falha ao carregar clientes do estágio", stageId, e);
        setError(e);
        setHasMoreByStage((m) => ({ ...m, [stageKeyStr]: false }));
      } finally {
        setLoadingByStage((m) => ({ ...m, [stageKeyStr]: false }));
      }
    },
    [filtersQuery, groupIdsByStage, limit, searchTerm, tipoPessoa]
  );

  useEffect(() => {
    const init = {};
    stageIds.forEach((id) => {
      init[String(id)] = [];
    });
    setClientsByStage(init);
    setPageByStage({});
    setHasMoreByStage({});
    setLoadingByStage({});
    setTotalByStage({});
    setError(null);

    stageIds.forEach((id) => {
      void loadPage(id, 1, true);
    });
  }, [filtersKey, limit, loadPage, stageIds, stageKey, searchTerm, tipoPessoa]);

  const loadMore = (stageId) => {
    const key = String(stageId);
    const p = pageByStage[key] || 1;
    if (!hasMoreByStage[key] || loadingByStage[key]) return;
    return loadPage(stageId, p + 1, false);
  };

  const refreshAll = async () => {
    await Promise.all(stageIds.map((id) => loadPage(id, 1, true)));
  };

  const patchClient = (clientId, patch) => {
    if (!clientId || !patch || typeof patch !== "object") return;
    setClientsByStage((prev) => {
      let changed = false;
      const next = {};
      Object.entries(prev).forEach(([key, list]) => {
        next[key] = (list || []).map((client) => {
          if (Number(client.id) !== Number(clientId)) return client;
          changed = true;
          return { ...client, ...patch };
        });
      });
      return changed ? next : prev;
    });
  };

  const moveClientBetweenStages = (fromStageId, toStageId, clientId) => {
    const fromKey = String(fromStageId);
    const toKey = String(toStageId);
    if (!fromKey || !toKey || !clientId) return;

    setClientsByStage((prev) => {
      const from = Array.isArray(prev[fromKey]) ? [...prev[fromKey]] : [];
      const to = Array.isArray(prev[toKey]) ? [...prev[toKey]] : [];
      const idx = from.findIndex((c) => Number(c.id) === Number(clientId));
      if (idx === -1) return prev;
      const [client] = from.splice(idx, 1);
      to.unshift({ ...client, id_grupo: toStageId === AWARENESS_UNSET_GROUP_STAGE_ID ? null : toStageId });
      return { ...prev, [fromKey]: from, [toKey]: to };
    });
    setTotalByStage((prev) => {
      if (prev[fromKey] == null && prev[toKey] == null) return prev;
      return {
        ...prev,
        [fromKey]: Math.max(0, (Number(prev[fromKey]) || 0) - 1),
        [toKey]: (Number(prev[toKey]) || 0) + 1,
      };
    });
  };

  const loading = useMemo(
    () => Object.values(loadingByStage).some(Boolean),
    [loadingByStage]
  );

  return {
    clientsByStage,
    loading,
    error,
    hasMoreByStage,
    loadingByStage,
    totalByStage,
    loadMore,
    moveClientBetweenStages,
    patchClient,
    refreshAll,
  };
};
